import { withCloudinaryTransform } from "./cloudinary";
import { streamSse } from "./sse";
import dialogStyles from "./dialog.css";
import { escapeHtml, renderMarkdown } from "./markdown";
import {
  clearConversationId,
  loadMessages,
  loadOrCreateConversationId,
  saveMessages,
} from "./storage";
import { accentContrastVars } from "./contrast";
import { themeOverridesCss } from "./theme";
import type {
  ChatMessage,
  ContactWidget,
  EmbedOptions,
  PublicAgentConfig,
} from "./types";

// One dialog instance per page lifecycle. Reopening is a no-op showModal().
let instance: DialogController | null = null;

export function openDialog(opts: EmbedOptions, config: PublicAgentConfig) {
  if (!instance) instance = new DialogController(opts, config);
  instance.open();
}

class DialogController {
  private host: HTMLElement;
  private shadow: ShadowRoot;
  private dialog!: HTMLDialogElement;
  private messagesEl!: HTMLDivElement;
  private inputEl!: HTMLTextAreaElement;
  private formEl!: HTMLFormElement;
  private honeypotEl!: HTMLInputElement;
  private sendBtn!: HTMLButtonElement;
  private startersEl!: HTMLDivElement;
  private bookingPanel!: HTMLDivElement;
  private bookingFrameWrap!: HTMLDivElement;
  private bookingFrame: HTMLIFrameElement | null = null;
  // The assistant turn whose calendar is open; flips to booked on Calendly's
  // calendly.event_scheduled message.
  private bookingMsg: ChatMessage | null = null;
  private messages: ChatMessage[];
  private streaming = false;
  private abort: AbortController | null = null;

  constructor(
    private readonly opts: EmbedOptions,
    private readonly config: PublicAgentConfig,
  ) {
    this.messages = loadMessages(opts.agent);

    this.host = document.createElement("div");
    // `font: inherit` lets dialog text pick up the host page's typography
    // through `all: initial` (which would otherwise reset font to UA default).
    this.host.style.cssText = "all: initial; font: inherit; position: fixed; inset: 0; pointer-events: none; z-index: 2147483600;";
    // Smooth-scroll libs (Lenis, Locomotive) hijack wheel/touch at window level.
    // The attribute is Lenis's escape hatch; stopPropagation covers the rest.
    this.host.setAttribute("data-lenis-prevent", "");
    document.body.appendChild(this.host);
    this.shadow = this.host.attachShadow({ mode: "closed" });

    this.render();

    // Calendly posts to its parent window, which is the host page (the shadow
    // root isn't a separate window). So host-page listeners, like a site's own
    // conversion pixel on event_scheduled, see these bookings too.
    window.addEventListener("message", (e) => {
      if (e.origin !== "https://calendly.com" || !this.bookingFrame) return;
      if (e.source !== this.bookingFrame.contentWindow) return;
      const data = e.data as { event?: unknown } | null;
      if (data?.event === "calendly.event_scheduled") this.markBooked();
    });
  }

  open() {
    // Per open, not once: the host page can swap its --clr-* palette (theme
    // toggle) while the dialog is closed.
    for (const [prop, value] of Object.entries(accentContrastVars(this.dialog))) {
      this.dialog.style.setProperty(prop, value);
    }
    if (!this.dialog.open) this.dialog.showModal();
    this.inputEl.focus();
  }

  // ⚠️ SYNC: dashboard/src/AppearancePreview.svelte mirrors the dialog DOM
  // built below — header (avatar/dot + title + clear/close icons), messages
  // area, starter pills row, form with input + send. If you change the SVG
  // paths, restructure the header, or move the starters/form around, update
  // the preview to match.
  private render() {
    const style = document.createElement("style");
    // Theme overrides come AFTER the main stylesheet so DB-configured colors
    // win over the :host fallbacks declared in dialog.css.
    style.textContent = dialogStyles + themeOverridesCss(this.config.themeOverrides);
    this.shadow.appendChild(style);

    this.dialog = document.createElement("dialog");
    this.dialog.className = "dialog";
    this.dialog.setAttribute("aria-label", this.config.dialogTitle);
    // Native light-dismiss: Esc + click-outside both close. Replaces a manual
    // backdrop-click handler. Safari support is still landing (Interop 2026);
    // older Safari falls back to Esc-only, which is acceptable here.
    this.dialog.setAttribute("closedby", "any");
    this.dialog.style.pointerEvents = "auto";
    // c_fill crops to a square at 2x of the 40px avatar circle (paired with
    // the CSS object-fit: cover). Small uploads get a modest upscale, which
    // is acceptable at this size; large uploads get a sharp downscale to ~80×80.
    const avatarHtml = this.config.triggerImageUrl
      ? `<img class="title-avatar" src="${escapeHtml(
          withCloudinaryTransform(
            this.config.triggerImageUrl,
            "c_fill,w_80,h_80,f_auto,q_auto",
          ),
        )}" alt="">`
      : "";
    this.dialog.innerHTML = `
      <header class="header">
        <span class="title">${avatarHtml}${escapeHtml(this.config.dialogTitle)}</span>
        <div class="actions">
          <button type="button" class="clear" aria-label="New chat" title="New chat">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
              <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </button>
          <button type="button" class="close" aria-label="Hide chat" title="Hide chat">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
            </svg>
          </button>
        </div>
      </header>
      <div class="messages" role="log" aria-live="polite"></div>
      <div class="booking-panel" hidden>
        <button type="button" class="booking-back">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
          Back to chat
        </button>
        <div class="booking-frame"></div>
      </div>
      <div class="starters"></div>
      <form class="form">
        <input type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
        <textarea class="input" placeholder="Type your question..." rows="1"></textarea>
        <button type="submit" class="send" aria-label="Send" disabled>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 19V5M5 12l7-7 7 7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
          </svg>
        </button>
      </form>
      ${this.config.showBranding ? `<a class="branding" href="https://chatporch.com/?utm_source=widget&utm_medium=badge&utm_campaign=${encodeURIComponent(this.config.agentKey)}" target="_blank" rel="noopener">Powered by <strong>Chatporch</strong></a>` : ""}
    `;
    this.shadow.appendChild(this.dialog);

    this.messagesEl = this.dialog.querySelector(".messages") as HTMLDivElement;
    this.startersEl = this.dialog.querySelector(".starters") as HTMLDivElement;
    this.inputEl = this.dialog.querySelector(".input") as HTMLTextAreaElement;
    this.formEl = this.dialog.querySelector(".form") as HTMLFormElement;
    this.honeypotEl = this.dialog.querySelector('input[name="website"]') as HTMLInputElement;
    this.sendBtn = this.dialog.querySelector(".send") as HTMLButtonElement;
    this.bookingPanel = this.dialog.querySelector(".booking-panel") as HTMLDivElement;
    this.bookingFrameWrap = this.dialog.querySelector(".booking-frame") as HTMLDivElement;
    this.dialog.querySelector(".booking-back")!.addEventListener("click", () => {
      this.closeBooking();
    });

    // Stop wheel/touch from reaching window-level smooth-scroll listeners
    // (Lenis et al.) so native scroll on .messages works.
    const stop = (e: Event) => e.stopPropagation();
    this.dialog.addEventListener("wheel", stop, { passive: true });
    this.dialog.addEventListener("touchmove", stop, { passive: true });

    this.dialog.querySelector(".close")!.addEventListener("click", () => {
      this.dialog.close();
    });
    this.dialog.querySelector(".clear")!.addEventListener("click", () => {
      this.clearChat();
    });
    // Fallback for browsers without `closedby` support (older Safari).
    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
    this.inputEl.addEventListener("input", () => {
      this.sendBtn.disabled = !this.inputEl.value.trim() || this.streaming;
    });
    this.inputEl.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.formEl.requestSubmit();
      }
    });
    this.formEl.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = this.inputEl.value.trim();
      if (!text || this.streaming) return;
      this.inputEl.value = "";
      this.sendBtn.disabled = true;
      void this.send(text);
    });

    this.renderMessages();
    this.renderStarters();
  }

  private clearChat() {
    this.abort?.abort();
    this.closeBooking();
    this.bookingFrame?.remove();
    this.bookingFrame = null;
    this.messages = [];
    saveMessages(this.opts.agent, this.messages);
    // Rotate the conversation ID so the next turn starts a fresh row in the
    // server-side log instead of appending to the visitor's prior transcript.
    clearConversationId(this.opts.agent);
    this.renderMessages();
    this.renderStarters();
    this.inputEl.focus();
  }

  private renderStarters() {
    this.startersEl.innerHTML = "";
    if (this.messages.length > 0 || this.config.starterPrompts.length === 0) {
      this.startersEl.style.display = "none";
      return;
    }
    this.startersEl.style.display = "flex";
    for (const starter of this.config.starterPrompts) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "starter";
      btn.textContent = starter.q;
      btn.addEventListener("click", () => {
        this.startersEl.style.display = "none";
        // Canned answer: render locally so the chip feels instant and we don't
        // spend a /v1/chat turn (or a model call) on a zero-cost response.
        if (starter.a && !starter.widget) this.sendCanned(starter.q, starter.a);
        else void this.send(starter.q);
      });
      this.startersEl.appendChild(btn);
    }
  }

  private sendCanned(question: string, answer: string) {
    this.messages.push({ role: "user", content: question });
    this.messages.push({ role: "assistant", content: answer });
    if (this.messagesEl.firstChild instanceof HTMLElement &&
        this.messagesEl.firstChild.classList.contains("empty")) {
      this.messagesEl.innerHTML = "";
    }
    this.messagesEl.appendChild(this.messageElement({ role: "user", content: question }));
    this.messagesEl.appendChild(this.messageElement({ role: "assistant", content: answer }));
    saveMessages(this.opts.agent, this.messages);
    this.scrollToBottom();
  }

  private renderMessages() {
    this.messagesEl.innerHTML = "";
    if (this.messages.length === 0) {
      const empty = document.createElement("div");
      empty.className = "empty";
      empty.textContent = "Hi! Ask me anything.";
      this.messagesEl.appendChild(empty);
      return;
    }
    for (const m of this.messages) {
      this.messagesEl.appendChild(this.messageElement(m));
    }
    this.scrollToBottom();
  }

  private messageElement(m: ChatMessage): HTMLDivElement {
    const el = document.createElement("div");
    el.className = `message message-${m.role}`;
    if (m.role === "assistant") this.fillAssistant(el, m);
    else el.textContent = m.content;
    return el;
  }

  private fillAssistant(el: HTMLElement, m: ChatMessage) {
    el.innerHTML = renderMarkdown(m.content);
    if (m.widget?.type === "booking") el.appendChild(this.bookingCard(m));
    if (m.widget?.type === "contact") el.appendChild(this.contactCard(m.widget));
  }

  private contactCard(w: ContactWidget): HTMLElement {
    const thanks = w.business
      ? `<strong>${escapeHtml(w.business)}</strong> will get back to you.`
      : "The team will get back to you.";
    if (w.submitted) {
      const done = document.createElement("div");
      done.className = "booking-card booked";
      done.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg><span class="booking-card-text"><strong>Sent</strong><span>${thanks}</span></span>`;
      return done;
    }

    const p = w.prefill;
    const form = document.createElement("form");
    form.className = "contact-card";
    form.noValidate = true;
    form.innerHTML = `
      <input type="text" name="website" tabindex="-1" autocomplete="off" aria-hidden="true">
      <label class="contact-field"><span>Name</span><input name="name" autocomplete="name" maxlength="100" value="${escapeHtml(p.name ?? "")}"></label>
      <div class="contact-row">
        <label class="contact-field"><span>Email</span><input name="email" type="email" autocomplete="email" maxlength="254" value="${escapeHtml(p.email ?? "")}"></label>
        <label class="contact-field"><span>Or phone</span><input name="phone" type="tel" autocomplete="tel" maxlength="40" value="${escapeHtml(p.phone ?? "")}"></label>
      </div>
      <label class="contact-field"><span>Message</span><textarea name="message" rows="3" maxlength="2000">${escapeHtml(p.message)}</textarea></label>
      <p class="contact-error" role="alert" hidden></p>
      <button type="submit" class="contact-send">Send</button>`;

    const errorEl = form.querySelector(".contact-error") as HTMLParagraphElement;
    const sendBtn = form.querySelector(".contact-send") as HTMLButtonElement;
    const value = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement).value.trim();
    const showError = (msg: string) => {
      errorEl.textContent = msg;
      errorEl.hidden = false;
    };

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (sendBtn.disabled) return;
      const lead = {
        name: value("name"),
        email: value("email"),
        phone: value("phone"),
        message: value("message"),
      };
      const invalid = validateLead(lead);
      if (invalid) return showError(invalid);
      errorEl.hidden = true;
      sendBtn.disabled = true;
      sendBtn.textContent = "Sending…";
      const fail = (msg?: string) => {
        showError(msg || "Couldn't send. Please try again.");
        sendBtn.disabled = false;
        sendBtn.textContent = "Send";
      };
      try {
        const res = await fetch(`${this.opts.apiBase}/v1/lead`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            agent: this.opts.agent,
            conversationId: loadOrCreateConversationId(this.opts.agent),
            ...lead,
            pageUrl: location.href,
            website: value("website"),
          }),
        });
        if (!res.ok) {
          // Field-validation 400s and 429s carry visitor-facing sentences;
          // "Invalid request" is the generic one and gets the retry copy.
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          const usable = (res.status === 400 || res.status === 429) && body.error !== "Invalid request";
          return fail(usable ? body.error : undefined);
        }
      } catch {
        return fail();
      }
      w.submitted = true;
      saveMessages(this.opts.agent, this.messages);
      form.replaceWith(this.contactCard(w));
      this.inputEl.focus();
    });
    return form;
  }

  private bookingCard(m: ChatMessage): HTMLElement {
    const booked = m.widget?.type === "booking" && !!m.widget.booked;
    const card = document.createElement(booked ? "div" : "button");
    card.className = booked ? "booking-card booked" : "booking-card";
    card.innerHTML = booked
      ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12l5 5 9-10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg><span class="booking-card-text"><strong>You're booked</strong><span>Check your email for the confirmation.</span></span>`
      : `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="2" fill="none"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg><span class="booking-card-text"><strong>Pick a time</strong><span>Opens the booking calendar</span></span><svg class="chevron" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
    if (!booked) {
      (card as HTMLButtonElement).type = "button";
      card.addEventListener("click", () => this.openBooking(m));
    }
    return card;
  }

  // Panel covers the message area and the dialog grows to near full height.
  // Calendly needs ~650px+ and would scroll within a scroll inside a bubble.
  // Calendly's own popup widget can't be used: it mounts on document.body,
  // underneath our top-layer modal, where it renders inert.
  private openBooking(m: ChatMessage) {
    if (m.widget?.type !== "booking") return;
    const src = schedulerSrc(m.widget.url);
    if (!this.bookingFrame || this.bookingFrame.dataset.src !== src) {
      this.bookingFrame?.remove();
      this.bookingFrameWrap.innerHTML = `<p class="booking-loading">Loading calendar… <a href="${escapeHtml(m.widget.url)}" target="_blank" rel="noopener">Open it in a new tab</a> if it doesn't appear.</p>`;
      const frame = document.createElement("iframe");
      frame.title = "Booking calendar";
      frame.src = src;
      frame.dataset.src = src;
      // Paid Calendly event types take payment in the frame.
      frame.allow = "payment";
      this.bookingFrameWrap.appendChild(frame);
      this.bookingFrame = frame;
    }
    this.bookingMsg = m;
    this.bookingPanel.hidden = false;
    this.dialog.classList.add("booking");
    (this.dialog.querySelector(".booking-back") as HTMLButtonElement).focus();
  }

  private closeBooking() {
    if (this.bookingPanel.hidden) return;
    this.bookingPanel.hidden = true;
    this.dialog.classList.remove("booking");
    this.scrollToBottom();
    this.inputEl.focus();
  }

  private markBooked() {
    const m = this.bookingMsg;
    if (m?.widget?.type !== "booking" || m.widget.booked) return;
    m.widget.booked = true;
    saveMessages(this.opts.agent, this.messages);
    // Panel stays open on Calendly's own confirmation screen; the card behind
    // it already shows the booked state when the visitor goes back.
    this.renderMessages();
  }

  private scrollToBottom() {
    this.messagesEl.scrollTo({
      top: this.messagesEl.scrollHeight,
      behavior: "smooth",
    });
  }

  private async send(text: string) {
    this.messages.push({ role: "user", content: text });
    this.renderStarters();
    if (this.messagesEl.firstChild instanceof HTMLElement &&
        this.messagesEl.firstChild.classList.contains("empty")) {
      this.messagesEl.innerHTML = "";
    }
    this.messagesEl.appendChild(this.messageElement({ role: "user", content: text }));

    const assistantMsg: ChatMessage = { role: "assistant", content: "" };
    this.messages.push(assistantMsg);
    const assistantEl = this.messageElement(assistantMsg);
    this.messagesEl.appendChild(assistantEl);
    this.scrollToBottom();

    this.streaming = true;
    this.sendBtn.disabled = true;
    this.abort = new AbortController();

    try {
      const res = await fetch(`${this.opts.apiBase}/v1/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agent: this.opts.agent,
          // Stable per-tab UUID; server uses it as the conversation row PK
          // so multi-turn chats group into one transcript in the dashboard.
          conversationId: loadOrCreateConversationId(this.opts.agent),
          messages: this.messages.slice(0, -1), // strip empty assistant placeholder
          // Per-request page context. Read at send time so SPA navigation
          // between turns is reflected. Worker length-caps both.
          pageUrl: location.href,
          pageTitle: document.title,
          website: this.honeypotEl.value,
        }),
        signal: this.abort.signal,
      });

      if (!res.ok) {
        // 429s carry optional visitor-facing copy from the Worker (billing
        // quota / daily burst — see server limitMessage). Render it verbatim
        // when present; otherwise fall back to the generic line.
        let serverMessage: string | undefined;
        try {
          const body = (await res.json()) as { message?: string };
          if (typeof body.message === "string") serverMessage = body.message;
        } catch {
          /* non-JSON error body — generic copy below */
        }
        const reason = res.status === 429 ? "rate_limited" : "error";
        throw new ChatRequestError(reason, serverMessage);
      }

      for await (const ev of streamSse(res, this.abort.signal)) {
        if (ev.event === "done") break;
        if (ev.event === "error") {
          throw new Error("stream_error");
        }
        try {
          if (ev.event === "widget") {
            const w = JSON.parse(ev.data) as {
              type?: string;
              url?: unknown;
              prefill?: ContactWidget["prefill"];
              business?: unknown;
            };
            if (w.type === "booking" && typeof w.url === "string" && w.url.startsWith("https://")) {
              assistantMsg.widget = { type: "booking", url: w.url };
            } else if (w.type === "contact" && w.prefill && typeof w.prefill.message === "string") {
              assistantMsg.widget = {
                type: "contact",
                prefill: w.prefill,
                ...(typeof w.business === "string" && w.business && { business: w.business }),
              };
            } else {
              continue;
            }
            this.fillAssistant(assistantEl, assistantMsg);
            this.scrollToBottom();
            continue;
          }
          const parsed = JSON.parse(ev.data) as { text?: string };
          if (parsed.text) {
            assistantMsg.content += parsed.text;
            this.fillAssistant(assistantEl, assistantMsg);
            this.scrollToBottom();
          }
        } catch {
          /* ignore malformed chunk */
        }
      }
    } catch (err) {
      const errorEl = document.createElement("div");
      errorEl.className = "message message-error";
      errorEl.textContent = this.errorText(err);
      this.messagesEl.appendChild(errorEl);
      // Roll back the placeholder so retry doesn't include an empty assistant turn.
      this.messages.pop();
      if (assistantEl.parentNode) assistantEl.remove();
    } finally {
      this.streaming = false;
      this.sendBtn.disabled = !this.inputEl.value.trim();
      saveMessages(this.opts.agent, this.messages);
      this.scrollToBottom();
    }
  }

  private errorText(err: unknown): string {
    if (err instanceof ChatRequestError && err.serverMessage) {
      return err.serverMessage;
    }
    const base = err instanceof Error && err.message === "rate_limited"
      ? "You've reached the message limit for now."
      : "Something went wrong.";
    return `${base} Please try again later.`;
  }
}

// Mirrors the Worker's checks in routes/lead.ts so most mistakes never
// round-trip. Returns visitor-facing copy, or null when the lead is sendable.
function validateLead(l: { name: string; email: string; phone: string }): string | null {
  if (!l.name) return "Please enter your name.";
  if (!l.email && !l.phone) return "Please enter an email or phone number so they can reach you.";
  if (l.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(l.email)) return "Please enter a valid email address.";
  const digits = l.phone.replace(/\D/g, "").length;
  if (l.phone && (!/^[\d\s()+.\-x]+$/i.test(l.phone) || digits < 7 || digits > 20)) {
    return "Please enter a valid phone number.";
  }
  return null;
}

// embed_domain must equal the host page's hostname or Calendly frames a blank
// page. The owner's own params (hide_gdpr_banner, primary_color, ...) survive.
function schedulerSrc(url: string): string {
  const u = new URL(url);
  if (!/(^|\.)calendly\.com$/i.test(u.hostname)) return url;
  u.searchParams.set("embed_domain", location.hostname);
  u.searchParams.set("embed_type", "Inline");
  return u.toString();
}

// Carries the Worker's visitor-facing copy alongside the coarse reason so
// errorText can prefer it. Server copy is complete sentences — rendered as-is.
class ChatRequestError extends Error {
  constructor(
    reason: "rate_limited" | "error",
    public serverMessage?: string,
  ) {
    super(reason);
  }
}

